import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NewDatePicker } from '../../../../../Components/PIM/common/NewDatePicker';
import { NewInput } from '../../../../../Components/PIM/common/NewInput';
import '../sample-tracking-dates.scss';
import { TrakingDatesProps, TrackingDateField, UserDetailsSlice } from '../store/Samples.type';

/**
 * Coerces a tracking-date field's stored value into the Date the picker expects.
 * The value arrives as an ISO string from the API, a Date once the user edits it,
 * or an array from the range-capable picker it replaced — anything unparseable
 * reads as an empty field rather than an Invalid Date.
 */
function toDateValue(value: TrackingDateField['value']): Date | null {
  if (!value || Array.isArray(value)) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * The bordered Sample Tracking Dates card: one row per active tracking stage, each a
 * fixed 200px date field plus — only on the stages configured to carry one — a Tracking
 * URL beside it. Values are written straight back through `trackingDatesUpdate`, which
 * is the react-hook-form field array the parent submits.
 */
function TrakingDates({ trackingDatesFields, trackingDatesRemove, selectedRequestType, trackingDatesUpdate }: TrakingDatesProps) {
  /* react-i18next@11.8 types t() as returning TFunctionResult, which @types/react@19 rejects
     as a ReactNode. TranslateFn is the shared cast declared in src/global.d.ts. */
  const { t: translate } = useTranslation('common');
  const t = translate as unknown as TranslateFn;
  const { isSupplierLoggedIn } = useSelector((redState: UserDetailsSlice) => redState.UserDetails);

  useEffect(() => {
    return () => {
      trackingDatesRemove();
    };
  }, []);

  if ((trackingDatesFields ?? []).length === 0) {
    return <div className="sample-tracking-box sample-tracking-empty">{t('DEVLOPMENT_ORDER.SAMPLES.TRACKING_DETES.NO_TRACKING_DATES')}</div>;
  }

  return (
    <div className="sample-tracking-box">
      {trackingDatesFields.map((item: TrackingDateField, index: number) => {
        const isRequestedOnRequest = (item?.refKey === 'requested' || item?.key === 'requested') && selectedRequestType?.value === 'request';
        const hasTrackingUrl = Boolean(item?.isTrakingURL || item?.trackingUrl || item?.sampleId?.isTrakingURL);
        const label = `${item?.name ?? ''} Date`;
        return (
          <div className="sample-tracking-row" key={item._id ?? index}>
            <div className="sample-tracking-field bombiix-input-wrap">
              <span className="bombiix-input__label">{label}</span>
              <NewDatePicker
                dateFormat="dd/MM/yyyy"
                placeholder={label}
                popperPlacement="top-end"
                disabled={isRequestedOnRequest || (Boolean(isSupplierLoggedIn) && !item?.isChecked)}
                value={toDateValue(item?.value)}
                onChange={(date) => trackingDatesUpdate(index, { ...item, value: date })}
              />
            </div>

            {hasTrackingUrl && (
              <div className="sample-tracking-row__url">
                <NewInput
                  label={t('DEVLOPMENT_ORDER.SAMPLES.TRACKING_DETES.TRACKING_URL')}
                  name={`trackingDates[${index}].trackingUrl`}
                  value={item?.trackingUrl ?? ''}
                  onChange={(event) => trackingDatesUpdate(index, { ...item, trackingUrl: event.target.value })}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default TrakingDates;
